import React, { useState, useEffect } from 'react';
import { __ } from './translations';

const BulkSupporterContributions = () => {
  const [supporters, setSupporters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedYear, setSelectedYear] = useState('');
  const [years, setYears] = useState([]);
  const [companiesStatus, setCompaniesStatus] = useState({});
  const [loadingStatus, setLoadingStatus] = useState(false);

  // Fetch available years
  useEffect(() => {
    const fetchYears = async () => {
      try {
        const response = await fetch(`${boardMemberData.restURL}wp/v2/year_data?orderby=title&order=desc`, {
          headers: {
            'X-WP-Nonce': boardMemberData.nonce
          }
        });
        const yearsData = await response.json();
        setYears(yearsData);
      } catch (error) {
        console.error('Error fetching years:', error);
      }
    };
    fetchYears();
  }, []);

  // Fetch supporter companies
  useEffect(() => {
    const fetchSupporters = async () => {
      try {
        const response = await fetch(`${boardMemberData.restURL}board-member/v1/supporter-companies`, {
          headers: {
            'X-WP-Nonce': boardMemberData.nonce
          }
        });
        const data = await response.json();
        setSupporters(data);
      } catch (error) {
        console.error('Error fetching supporters:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSupporters();
  }, []);

  // Check contribution status when year changes
  useEffect(() => {
    if (selectedYear && supporters.length > 0) {
      checkContributionStatus();
    }
  }, [selectedYear, supporters]);

  // Check which companies already have contributions for the selected year
  const checkContributionStatus = async () => {
    if (!selectedYear) return;
    
    setLoadingStatus(true);
    try {
      const response = await fetch(`${boardMemberData.restURL}board-member/v1/supporter-contribution-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': boardMemberData.nonce
        },
        body: JSON.stringify({
          year_id: selectedYear,
          company_ids: supporters.map(s => s.id)
        })
      });
      
      const statusData = await response.json();
      setCompaniesStatus(statusData);
    } catch (error) {
      console.error('Error checking contribution status:', error);
    } finally {
      setLoadingStatus(false);
    }
  };

  const handleBulkContribution = async () => {
    if (!selectedYear) {
      setResult({
        success: false,
        message: __('Please select a year first')
      });
      return;
    }

    setProcessing(true);
    setResult(null);

    try {
      const response = await fetch(`${boardMemberData.restURL}board-member/v1/bulk-supporter-contributions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': boardMemberData.nonce
        },
        body: JSON.stringify({
          year_id: selectedYear
        })
      });

      const data = await response.json();
      setResult({
        success: data.success,
        message: data.message,
        details: data.details
      });

      // Refresh the status after processing
      if (data.success) {
        checkContributionStatus();
      }
    } catch (error) {
      setResult({
        success: false,
        message: 'Failed to process bulk contributions'
      });
    } finally {
      setProcessing(false);
    }
  };

  // Get statistics
  const getStatistics = () => {
    if (!selectedYear || Object.keys(companiesStatus).length === 0) {
      return { total: supporters.length, existing: 0, new: supporters.length };
    }

    const existing = supporters.filter(supporter => 
      companiesStatus[supporter.id]?.hasContribution
    ).length;
    
    return {
      total: supporters.length,
      existing: existing,
      new: supporters.length - existing
    };
  };

  // Get companies that will receive emails (new contributions only)
  const getEmailRecipients = () => {
    if (!selectedYear || Object.keys(companiesStatus).length === 0) {
      return supporters;
    }

    return supporters.filter(supporter => 
      !companiesStatus[supporter.id]?.hasContribution
    );
  };

  const stats = getStatistics();
  const emailRecipients = getEmailRecipients();

  if (!boardMemberData.isAdmin) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">{__('Access Denied')}</strong>
        <span className="block sm:inline"> {__('Only administrators can access this feature.')}</span>
      </div>
    );
  }

  if (loading) {
    return <div className="text-center p-4">{__('Loading supporter data...')}</div>;
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-2xl font-bold mb-6">{__('Bulk Supporter Contributions Manager')}</h2>
        
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">{__('Select Year:')}</label>
          <select 
            className="w-full p-2 border rounded-md"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            <option value="">{__('Select a year')}</option>
            {years.map((year) => (
              <option key={year.id} value={year.id}>
                {year.title.rendered}
              </option>
            ))}
          </select>
        </div>

        {/* Statistics Panel */}
        {selectedYear && (
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-800">{__('Total Companies')}</h4>
              <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-800">{__('Already Generated')}</h4>
              <p className="text-2xl font-bold text-green-600">{stats.existing}</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <h4 className="font-semibold text-orange-800">{__('New Contributions')}</h4>
              <p className="text-2xl font-bold text-orange-600">{stats.new}</p>
            </div>
          </div>
        )}

        {/* Email Recipients Information */}
        {selectedYear && emailRecipients.length > 0 && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-semibold text-yellow-800 mb-2">
              ⚠️ {__('Email Notification Information')}
            </h4>
            <p className="text-yellow-700 mb-2">
              {__('The following companies will receive email notifications when you generate contributions:')}
            </p>
            <div className="max-h-32 overflow-y-auto">
              <ul className="text-sm text-yellow-700">
                {emailRecipients.map((company) => (
                  <li key={company.id} className="flex items-center">
                    <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></span>
                    {company.name}
                    {companiesStatus[company.id]?.userEmail && (
                      <span className="ml-2 text-xs text-yellow-600">
                        ({companiesStatus[company.id].userEmail})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-semibold">
              {__('Supporter Companies')} ({supporters.length})
            </h3>
            {loadingStatus && (
              <span className="text-sm text-blue-600">{__('Checking status...')}</span>
            )}
          </div>
          
          <div className="bg-gray-50 p-4 rounded-md max-h-64 overflow-y-auto">
            <ul className="divide-y divide-gray-200">
              {supporters.map((supporter) => {
                const status = companiesStatus[supporter.id];
                const hasContribution = status?.hasContribution;
                
                return (
                  <li key={supporter.id} className="py-2 flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="font-medium">{supporter.name}</span>
                      {selectedYear && (
                        <span className={`ml-3 px-2 py-1 text-xs font-semibold rounded-full ${
                          hasContribution 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-orange-100 text-orange-800'
                        }`}>
                          {hasContribution ? __('Generated') : __('New')}
                        </span>
                      )}
                    </div>
                    {selectedYear && status?.userEmail && (
                      <span className="text-xs text-gray-500">{status.userEmail}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Warning for existing contributions */}
        {selectedYear && stats.existing > 0 && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-800 mb-2">
              ℹ️ {__('Important Notice')}
            </h4>
            <p className="text-blue-700">
              {stats.existing === 1 
                ? __('1 company already has a contribution generated for this year and will be skipped.')
                : `${stats.existing} ${__('companies already have contributions generated for this year and will be skipped.')}`
              }
            </p>
            {stats.new === 0 && (
              <p className="text-blue-700 font-semibold mt-2">
                {__('All companies have already generated contributions for this year.')}
              </p>
            )}
          </div>
        )}

        <button
          onClick={handleBulkContribution}
          disabled={processing || !selectedYear || (selectedYear && stats.new === 0)}
          className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          {processing 
            ? __('Processing...') 
            : selectedYear && stats.new === 0
              ? __('No new contributions to generate')
              : stats.new > 0 
                ? `${__('Generate Contributions for')} ${stats.new} ${__('New Companies')}`
                : __('Generate Contributions for All Supporters')
          }
        </button>

        {result && (
          <div className={`mt-4 p-4 rounded-md ${result.success ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
            <p className="font-semibold mb-2">{result.success ? __('Success') : __('Error')}</p>
            <p>{result.message}</p>
            {result.details && (
              <ul className="mt-2 list-disc list-inside">
                {result.details.map((detail, index) => (
                  <li key={index}>{detail}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkSupporterContributions;